/**
 * Cube Slider — frontend bootstrap (Embla + controls registry + cube effect).
 *
 * Reuses the globally-enqueued classic scripts the same way Slider/view.js
 * does: `brandy-embla-script` (window.EmblaCarousel), `brandy-embla-autoplay`
 * (window.EmblaCarouselAutoplay), `brandy-embla-registry` (window.brandyEmbla)
 * and `brandy-embla-effect-cube` (window.brandyEmblaCube). Module scripts can
 * run before deferred classic scripts, so each global is awaited with a short
 * poll.
 *
 * No auto-scroll branch, unlike Slider: continuous drift never settles on a
 * snap, so this block has no `autoplayMode` and always pages discretely.
 *
 * The bootstrap duplicates Slider/view.js rather than sharing it, matching how
 * ShowcaseSlider already carries its own. Extracting a common bootstrap is a
 * worthwhile follow-up but would touch three shipping blocks at once.
 *
 * @package BrandyBlocks
 */

const CUBE_SELECTOR = '[data-brandy-cube-slider]';

/**
 * Poll for a window global defined by a deferred classic script.
 * Resolves the global or null after timeout (with a console warning).
 */
async function waitForGlobal(name, timeoutMs = 2000) {
  const start = Date.now();
  while (!window[name]) {
    if (Date.now() - start > timeoutMs) {
      // eslint-disable-next-line no-console
      console.warn(`[brandy/cube-slider] ${name} global not found within timeout`);
      return null;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  return window[name];
}

async function initCubeSlider(rootEl) {
  const Embla = await waitForGlobal('EmblaCarousel');
  if (!Embla) return;

  const emblaNode = rootEl.querySelector('.embla');
  const viewportNode = rootEl.querySelector('.embla__viewport');
  if (!emblaNode || !viewportNode) return;

  const loop = emblaNode.dataset.loop !== 'false';

  const registry = await waitForGlobal('brandyEmbla');
  const autoplayConfig = registry
    ? registry.readAutoplayConfig(emblaNode, { delay: 4000 })
    : {
        enabled: emblaNode.dataset.autoplay === 'true',
        delay: parseInt(emblaNode.dataset.autoplayDelay, 10) || 4000,
        pauseOnHover: emblaNode.dataset.pauseHover === 'true',
      };

  const plugins = [];
  if (autoplayConfig.enabled) {
    const Autoplay = await waitForGlobal('EmblaCarouselAutoplay');
    if (Autoplay) {
      plugins.push(
        Autoplay({
          delay: autoplayConfig.delay,
          stopOnInteraction: true,
          // Hover belongs to the registry helper attached after init — see the
          // Slider block's view.js for why the plugin's own version cannot do it.
          stopOnMouseEnter: false,
        })
      );
    }
    // Missing plugin global degrades to a manual slider — already warned.
  }

  // Drop the no-JS scroll fallback BEFORE init: an overflow-x:auto container
  // clips its own slides, which breaks Embla's measurements. Reset any scroll
  // performed pre-init so face 1 is the measured origin.
  const containerNode = emblaNode.querySelector('.embla__container');
  if (containerNode) containerNode.scrollLeft = 0;
  rootEl.classList.add('is-embla-ready');

  const api = Embla(viewportNode, { loop, align: 'start' }, plugins);

  /*
   * The cube itself. Attached directly rather than through a registry
   * namespace — one effect used by one block does not earn the indirection.
   * Optional-chained for the same reason the rest of the registry is: a
   * timed-out global should cost the slider its effect, not its navigation.
   */
  const cube = await waitForGlobal('brandyEmblaCube');
  const detachCube = cube?.attach(api, emblaNode);
  if (typeof detachCube === 'function') api.on('destroy', detachCube);

  /*
   * Hover pause that holds the remaining delay, so the progress fill and the
   * face it counts down agree on when the next one is due.
   */
  if (autoplayConfig.pauseOnHover) {
    const detachHoverPause = registry?.attachHoverPause?.(api, viewportNode);
    if (typeof detachHoverPause === 'function') api.on('destroy', detachHoverPause);
  }

  // Nothing to scroll: hide the chrome rather than show dead controls.
  if (api.scrollSnapList().length <= 1) {
    emblaNode.querySelector('.embla__buttons')?.style.setProperty('display', 'none');
    emblaNode.querySelector('.embla__controls')?.style.setProperty('display', 'none');
    emblaNode.querySelector('.embla__dots')?.style.setProperty('display', 'none');
    return;
  }

  if (!registry) return;

  // Gate on the buttons themselves, not their wrapper: a grouped placement
  // renders them inside .embla__controls with no .embla__buttons at all.
  if (emblaNode.querySelector('.embla__button--prev')) {
    const navFn = registry.nav.resolve(emblaNode.getAttribute('data-nav-style') || 'default');
    if (navFn) {
      const cleanup = navFn(api, emblaNode);
      if (typeof cleanup === 'function') api.on('destroy', cleanup);
    }
  }
  if (emblaNode.querySelector('.embla__dots')) {
    const dotsFn = registry.dots.resolve(emblaNode.getAttribute('data-dots-style') || 'default');
    if (dotsFn) {
      const cleanup = dotsFn(api, emblaNode);
      if (typeof cleanup === 'function') api.on('destroy', cleanup);
    }
  }
}

function bootstrap() {
  document.querySelectorAll(CUBE_SELECTOR).forEach((el) => {
    initCubeSlider(el).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[brandy/cube-slider] Embla init failed', err);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
